import React, { useCallback, useMemo, useState } from 'react';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { useUpdateBundle } from '~/hooks/apis/accommodation/useBundles';

import { Bundle } from '~/services/AccommodationService';

import BundlePropertyPicker, { PropertyOption } from '../components/BundlePropertyPicker';

interface Props {
  bundle: Bundle;
}

function AccommodationBundleHotelsTab({ bundle }: Props) {
  const initialIds = useMemo(
    () =>
      (bundle.offers ?? []).filter((o) => o.entityType === 'HOTEL' && o.propertyId).map((o) => o.propertyId as string),
    [bundle.offers],
  );

  const [selected, setSelected] = useState<Array<PropertyOption>>(() => initialIds.map((id) => ({ id, name: id })));
  const [conflictPropertyIds, setConflictPropertyIds] = useState<Array<string>>([]);

  const updateBundleMutation = useUpdateBundle();

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setConflictPropertyIds([]);
      const propertyIds = selected.map((p) => p.id);

      try {
        await updateBundleMutation.mutateAsync({
          bundleId: bundle.id,
          payload: { propertyIds },
          errorBodyCallback: (body) => {
            const conflicts =
              (body as { result?: { conflictingPropertyIds?: Array<string> } } | undefined)?.result
                ?.conflictingPropertyIds ?? [];
            if (conflicts.length > 0) {
              setConflictPropertyIds(conflicts);
            }
          },
        });
      } catch {
        // Non-409 errors are surfaced via the hook's snackbar; 409 conflicts render inline above.
      }
    },
    [selected, updateBundleMutation, bundle.id],
  );

  const isSubmitting = updateBundleMutation.isPending;

  return (
    <Box component="form" onSubmit={handleSave}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Search and select properties to attach to this bundle. Replaces the entire HOTEL routing on save.
        </Typography>

        {conflictPropertyIds.length > 0 && (
          <Alert severity="error">
            One or more property IDs are already attached to another bundle: {conflictPropertyIds.join(', ')}
          </Alert>
        )}

        <BundlePropertyPicker value={selected} onChange={setSelected} initialIds={initialIds} />

        <Box display="flex" justifyContent="flex-end">
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save Hotels'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default AccommodationBundleHotelsTab;
